/**
 * EVENTSFLO-TASK-DEFINITIONS-PHASE-2.2-DELETE-MERGE-CLEANUP-V1
 *
 * SuperAdmin-only Task Definition cleanup tooling. Scan,
 * archive/delete (dry-run + exact phrase), merge (dry-run +
 * exact phrase). Historical snapshots are preserved; travel/OOT
 * and rule-critical TDs are protected.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import {
  AlertTriangle,
  Archive,
  CheckCircle2,
  Database,
  Download,
  GitMerge,
  History,
  Info,
  Lock,
  Play,
  RefreshCw,
  ShieldAlert,
  Trash2,
} from "lucide-react";
import { useAuth } from "../../../context/AuthContext";

const ENDPOINT = "/api/admin/task-definitions/cleanup";
const APPLY_CLEANUP = "CLEAN UP TASK DEFINITIONS";
const APPLY_MERGE = "MERGE TASK DEFINITIONS";

async function authedFetch(path, token, options = {}) {
  const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
  });
  let body = null;
  try { body = await r.json(); } catch { body = null; }
  if (!r.ok) {
    const detail = body?.detail || body || { error: `HTTP ${r.status}` };
    const msg =
      typeof detail === "string"
        ? detail
        : detail.message || detail.error || `HTTP ${r.status}`;
    const err = new Error(msg);
    err.status = r.status;
    throw err;
  }
  return body;
}

export default function TaskDefinitionsCleanupCard() {
  const { user, token } = useAuth();
  const isSuperAdmin = user?.role === "superadmin";

  const [scan, setScan] = useState(null);
  const [scanLoading, setScanLoading] = useState(false);

  const [selectedIds, setSelectedIds] = useState(new Set());
  const [mode, setMode] = useState("archive");
  const [dryRunResult, setDryRunResult] = useState(null);
  const [cleanupConfirm, setCleanupConfirm] = useState("");

  const [mergeSource, setMergeSource] = useState("");
  const [mergeTarget, setMergeTarget] = useState("");
  const [mergeDryRun, setMergeDryRun] = useState(null);
  const [mergeConfirm, setMergeConfirm] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  // EVENTSFLO-TASK-DEFINITIONS-CLEANUP-AUDIT-LOG-PANEL-V1
  // Read-only recent maintenance audit log (last 20).
  const [auditLog, setAuditLog] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);

  const fetchAuditLog = useCallback(async () => {
    if (!isSuperAdmin || !token) return;
    setAuditLoading(true);
    try {
      const body = await authedFetch(
        `${ENDPOINT}/audit-log?limit=20`,
        token,
        { method: "GET" },
      );
      setAuditLog(body);
    } catch (e) {
      toast.error(e.message || "Audit log fetch failed");
    } finally {
      setAuditLoading(false);
    }
  }, [isSuperAdmin, token]);

  // EVENTSFLO-TASK-DEFINITIONS-CLEANUP-AUDIT-CSV-EXPORT-V1
  // Read-only CSV download (last 50 rows). Reuses the safe
  // projection of the audit-log endpoint. No undo/rollback.
  const [csvExporting, setCsvExporting] = useState(false);

  const exportAuditCsv = useCallback(async () => {
    if (!isSuperAdmin || !token) return;
    setCsvExporting(true);
    try {
      const r = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}${ENDPOINT}/audit-log/export.csv?limit=50`,
        {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (!r.ok) {
        throw new Error(`CSV export failed (${r.status})`);
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "task-definition-cleanup-audit-log.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Audit log CSV downloaded");
    } catch (e) {
      toast.error(e.message || "CSV export failed");
    } finally {
      setCsvExporting(false);
    }
  }, [isSuperAdmin, token]);

  const runScan = useCallback(async () => {
    if (!isSuperAdmin || !token) return;
    setScanLoading(true);
    try {
      const body = await authedFetch(`${ENDPOINT}/scan`, token, {
        method: "POST",
        body: JSON.stringify({ include_inactive: true, include_travel: true }),
      });
      setScan(body);
      setSelectedIds(new Set());
      setDryRunResult(null);
      toast.success(
        `Scan: ${body.total_td_scanned} TDs · ${body.duplicate_groups.length} duplicate groups · ${body.unused_definitions.length} unused · ${body.protected_definitions.length} protected`,
      );
    } catch (e) {
      toast.error(e.message || "Scan failed");
    } finally {
      setScanLoading(false);
    }
  }, [isSuperAdmin, token]);

  const toggleId = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const runCleanupDryRun = async () => {
    if (selectedIds.size === 0) {
      toast.error("Select at least one Task Definition.");
      return;
    }
    setActionLoading(true);
    try {
      const body = await authedFetch(`${ENDPOINT}/archive-delete`, token, {
        method: "POST",
        body: JSON.stringify({
          dry_run: true, mode,
          task_definition_ids: Array.from(selectedIds),
        }),
      });
      setDryRunResult(body);
      toast.success(
        `Dry-run (${mode}): ${body.eligible_ids.length} eligible · ${body.blocked_ids.length} blocked`,
      );
    } catch (e) {
      toast.error(e.message || "Dry-run failed");
    } finally {
      setActionLoading(false);
    }
  };

  const applyCleanup = async () => {
    if (cleanupConfirm !== APPLY_CLEANUP) {
      toast.error("Confirmation phrase does not match.");
      return;
    }
    setActionLoading(true);
    try {
      const body = await authedFetch(`${ENDPOINT}/archive-delete`, token, {
        method: "POST",
        body: JSON.stringify({
          dry_run: false, mode,
          task_definition_ids: Array.from(selectedIds),
          confirm: APPLY_CLEANUP,
        }),
      });
      toast.success(
        `${mode === "archive" ? "Archived" : "Deleted"} ${
          mode === "archive" ? body.archived_count : body.deleted_count
        } TD(s)`,
      );
      setCleanupConfirm("");
      setSelectedIds(new Set());
      runScan();
    } catch (e) {
      toast.error(e.message || "Apply failed");
    } finally {
      setActionLoading(false);
    }
  };

  const runMergeDryRun = async () => {
    if (!mergeSource.trim() || !mergeTarget.trim()) {
      toast.error("Source and target IDs required.");
      return;
    }
    setActionLoading(true);
    try {
      const body = await authedFetch(`${ENDPOINT}/merge`, token, {
        method: "POST",
        body: JSON.stringify({
          dry_run: true,
          source_id: mergeSource.trim(),
          target_id: mergeTarget.trim(),
        }),
      });
      setMergeDryRun(body);
      if (body.blocked) {
        toast.warning(`Merge blocked — ${body.blockers.length} blocker(s)`);
      } else {
        toast.success(
          `Merge dry-run: would repoint ${body.active_references_to_repoint} active refs`,
        );
      }
    } catch (e) {
      toast.error(e.message || "Merge dry-run failed");
    } finally {
      setActionLoading(false);
    }
  };

  const applyMerge = async () => {
    if (mergeConfirm !== APPLY_MERGE) {
      toast.error("Confirmation phrase does not match.");
      return;
    }
    setActionLoading(true);
    try {
      const body = await authedFetch(`${ENDPOINT}/merge`, token, {
        method: "POST",
        body: JSON.stringify({
          dry_run: false,
          source_id: mergeSource.trim(),
          target_id: mergeTarget.trim(),
          confirm: APPLY_MERGE,
        }),
      });
      toast.success(
        `Merge applied — ${body.updated_counts_by_collection?.shift_task_line_items || 0} line items repointed`,
      );
      setMergeConfirm("");
      setMergeDryRun(null);
      runScan();
    } catch (e) {
      toast.error(e.message || "Merge apply failed");
    } finally {
      setActionLoading(false);
    }
  };

  const cleanupApplyEnabled = useMemo(
    () =>
      cleanupConfirm === APPLY_CLEANUP &&
      !actionLoading &&
      dryRunResult &&
      dryRunResult.eligible_ids.length > 0,
    [cleanupConfirm, actionLoading, dryRunResult],
  );
  const mergeApplyEnabled = useMemo(
    () =>
      mergeConfirm === APPLY_MERGE &&
      !actionLoading &&
      mergeDryRun &&
      !mergeDryRun.blocked,
    [mergeConfirm, actionLoading, mergeDryRun],
  );

  useEffect(() => {
    fetchAuditLog();
  }, [fetchAuditLog]);

  const formatWhen = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return iso;
      return d.toLocaleString();
    } catch {
      return iso;
    }
  };

  if (!isSuperAdmin) return null;

  return (
    <Card data-testid="task-definitions-cleanup-card">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Database className="w-4 h-4" /> Task Definitions cleanup
        </CardTitle>
        <p className="text-xs text-zinc-400 mt-1">
          SuperAdmin-only. Detect duplicates and unused Task
          Definitions, then{" "}
          <strong className="text-zinc-300">archive, delete or
          merge</strong>{" "}
          with dry-run and exact confirmation. Historical
          quote/invoice/task snapshots are preserved.
        </p>
      </CardHeader>
      <CardContent>
        {/* Snapshot-preserved banner */}
        <div
          className="rounded-md border border-amber-500/30 bg-amber-500/5 p-2 text-xs text-amber-200 flex items-start gap-2"
          data-testid="td-cleanup-snapshot-preserved-note"
        >
          <ShieldAlert className="w-3.5 h-3.5 mt-0.5" />
          <span>
            Historical quote/invoice/task snapshots are preserved
            and not rewritten. Travel/OOT and rule-critical Task
            Definitions are protected.
          </span>
        </div>

        {/* Scan */}
        <div className="mt-3 rounded-md border border-zinc-700/40 p-3" data-testid="td-cleanup-scan-section">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-medium text-zinc-300 flex items-center gap-2">
              <Info className="w-3.5 h-3.5" /> Scan
            </div>
            <Button
              variant="outline"
              onClick={runScan}
              disabled={scanLoading}
              data-testid="td-cleanup-scan-btn"
            >
              <Play className="w-3.5 h-3.5 mr-1" /> Run scan
            </Button>
          </div>
          {!scan ? (
            <div className="text-xs text-zinc-500">
              Click <strong>Run scan</strong> to inspect duplicates,
              unused and protected Task Definitions.
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs mb-2">
                <div>
                  <div className="text-zinc-400">scanned</div>
                  <div className="text-zinc-300" data-testid="td-cleanup-scan-total">
                    {scan.total_td_scanned}
                  </div>
                </div>
                <div>
                  <div className="text-zinc-400">duplicate groups</div>
                  <div className="text-zinc-300" data-testid="td-cleanup-scan-dup-count">
                    {scan.duplicate_groups.length}
                  </div>
                </div>
                <div>
                  <div className="text-zinc-400">unused</div>
                  <div className="text-zinc-300" data-testid="td-cleanup-scan-unused-count">
                    {scan.unused_definitions.length}
                  </div>
                </div>
                <div>
                  <div className="text-zinc-400">protected</div>
                  <div className="text-zinc-300" data-testid="td-cleanup-scan-protected-count">
                    {scan.protected_definitions.length}
                  </div>
                </div>
              </div>

              {scan.duplicate_groups.length > 0 ? (
                <div className="mb-3" data-testid="td-cleanup-duplicate-groups">
                  <div className="text-xs text-zinc-400 mb-1">Duplicate groups</div>
                  {scan.duplicate_groups.map((g, gi) => (
                    <div
                      key={`${g.normalised_name}-${gi}`}
                      className="rounded border border-zinc-700/40 p-2 mb-2"
                      data-testid={`td-cleanup-dup-group-${gi}`}
                    >
                      <div className="text-xs text-zinc-300 mb-1 font-mono">
                        {g.normalised_name}
                        {g.supplement_group ? ` · sg=${g.supplement_group}` : ""}
                      </div>
                      <table className="w-full text-xs">
                        <thead className="bg-zinc-800/60 text-zinc-400">
                          <tr>
                            <th className="text-left px-2 py-1">sel</th>
                            <th className="text-left px-2 py-1">id</th>
                            <th className="text-left px-2 py-1">name</th>
                            <th className="text-left px-2 py-1">refs</th>
                            <th className="text-left px-2 py-1">flags</th>
                          </tr>
                        </thead>
                        <tbody>
                          {g.members.map((m) => (
                            <tr
                              key={m.id}
                              className="odd:bg-zinc-900/30"
                              data-testid={`td-cleanup-dup-row-${m.id}`}
                            >
                              <td className="px-2 py-1">
                                <input
                                  type="checkbox"
                                  checked={selectedIds.has(m.id)}
                                  onChange={() => toggleId(m.id)}
                                  disabled={m.is_protected}
                                  data-testid={`td-cleanup-select-${m.id}`}
                                />
                              </td>
                              <td className="px-2 py-1 font-mono text-zinc-300">
                                {m.id.slice(0, 8)}…
                              </td>
                              <td className="px-2 py-1 text-zinc-300">{m.name}</td>
                              <td className="px-2 py-1 text-zinc-300">
                                act:{m.active_reference_count} hist:
                                {m.historical_reference_count}
                              </td>
                              <td className="px-2 py-1">
                                {m.is_protected ? (
                                  <Badge className="bg-rose-500/15 text-rose-300 border border-rose-500/30">
                                    protected
                                  </Badge>
                                ) : (
                                  <Badge className="bg-zinc-500/15 text-zinc-300 border border-zinc-500/30">
                                    eligible
                                  </Badge>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ))}
                </div>
              ) : null}

              {scan.unused_definitions.length > 0 ? (
                <div className="mb-3" data-testid="td-cleanup-unused-list">
                  <div className="text-xs text-zinc-400 mb-1">Unused Task Definitions</div>
                  <div className="overflow-auto rounded border border-zinc-700/40 max-h-48">
                    <table className="w-full text-xs">
                      <thead className="bg-zinc-800/60 text-zinc-400 sticky top-0">
                        <tr>
                          <th className="text-left px-2 py-1">sel</th>
                          <th className="text-left px-2 py-1">id</th>
                          <th className="text-left px-2 py-1">name</th>
                          <th className="text-left px-2 py-1">flags</th>
                        </tr>
                      </thead>
                      <tbody>
                        {scan.unused_definitions.map((m) => (
                          <tr
                            key={m.id}
                            className="odd:bg-zinc-900/30"
                            data-testid={`td-cleanup-unused-row-${m.id}`}
                          >
                            <td className="px-2 py-1">
                              <input
                                type="checkbox"
                                checked={selectedIds.has(m.id)}
                                onChange={() => toggleId(m.id)}
                                disabled={m.is_protected}
                                data-testid={`td-cleanup-unused-select-${m.id}`}
                              />
                            </td>
                            <td className="px-2 py-1 font-mono text-zinc-300">
                              {m.id.slice(0, 8)}…
                            </td>
                            <td className="px-2 py-1 text-zinc-300">{m.name}</td>
                            <td className="px-2 py-1">
                              {m.is_protected ? (
                                <Badge className="bg-rose-500/15 text-rose-300 border border-rose-500/30">
                                  protected
                                </Badge>
                              ) : (
                                <Badge className="bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
                                  unused
                                </Badge>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : null}
            </>
          )}
        </div>

        {/* Archive / Delete */}
        <div className="mt-3 rounded-md border border-zinc-700/40 p-3" data-testid="td-cleanup-archive-delete-section">
          <div className="text-sm font-medium text-zinc-300 mb-2 flex items-center gap-2">
            <Archive className="w-3.5 h-3.5" /> Archive or delete selected
          </div>
          <div className="flex flex-wrap items-end gap-3 mb-2">
            <div>
              <div className="text-xs text-zinc-400 mb-1">mode</div>
              <div className="inline-flex gap-1">
                <Button
                  size="sm"
                  variant={mode === "archive" ? "default" : "outline"}
                  onClick={() => setMode("archive")}
                  data-testid="td-cleanup-mode-archive-btn"
                >
                  <Archive className="w-3.5 h-3.5 mr-1" /> Archive
                </Button>
                <Button
                  size="sm"
                  variant={mode === "delete" ? "default" : "outline"}
                  onClick={() => setMode("delete")}
                  data-testid="td-cleanup-mode-delete-btn"
                >
                  <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                </Button>
              </div>
            </div>
            <div className="text-xs text-zinc-400">
              selected: <strong className="text-zinc-300" data-testid="td-cleanup-selected-count">{selectedIds.size}</strong>
            </div>
            <Button
              variant="outline"
              onClick={runCleanupDryRun}
              disabled={actionLoading || selectedIds.size === 0}
              data-testid="td-cleanup-dry-run-btn"
            >
              <Play className="w-3.5 h-3.5 mr-1" /> Dry-run
            </Button>
          </div>

          {dryRunResult ? (
            <div
              className="rounded border border-zinc-700/40 bg-zinc-900/40 p-2 text-xs grid grid-cols-2 md:grid-cols-4 gap-2 mb-2"
              data-testid="td-cleanup-dry-run-result"
            >
              <div>
                <div className="text-zinc-400">eligible</div>
                <div className="text-zinc-300">{dryRunResult.eligible_ids.length}</div>
              </div>
              <div>
                <div className="text-zinc-400">blocked</div>
                <div className="text-zinc-300">{dryRunResult.blocked_ids.length}</div>
              </div>
              <div>
                <div className="text-zinc-400">mode</div>
                <div className="text-zinc-300">{dryRunResult.mode}</div>
              </div>
              <div>
                <div className="text-zinc-400">production_mutation</div>
                <div className="text-zinc-300">{String(dryRunResult.production_mutation)}</div>
              </div>
            </div>
          ) : null}

          <div className="mt-2">
            <Label className="text-xs text-zinc-400" htmlFor="td-cleanup-confirm-input">
              Type exactly to enable Apply:{" "}
              <span className="font-mono text-amber-300">{APPLY_CLEANUP}</span>
            </Label>
            <div className="flex flex-wrap gap-2 items-center mt-1">
              <Input
                id="td-cleanup-confirm-input"
                value={cleanupConfirm}
                onChange={(e) => setCleanupConfirm(e.target.value)}
                placeholder={APPLY_CLEANUP}
                className="max-w-md font-mono text-xs"
                data-testid="td-cleanup-confirm-input"
              />
              <Button
                onClick={applyCleanup}
                disabled={!cleanupApplyEnabled}
                data-testid="td-cleanup-apply-btn"
              >
                <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Apply {mode}
              </Button>
              {!cleanupApplyEnabled ? (
                <div
                  className="text-xs text-zinc-400 flex items-center gap-1"
                  data-testid="td-cleanup-apply-helper"
                >
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />
                  Dry-run with eligible items, then exact phrase required.
                </div>
              ) : null}
            </div>
          </div>
        </div>

        {/* Merge */}
        <div className="mt-3 rounded-md border border-zinc-700/40 p-3" data-testid="td-cleanup-merge-section">
          <div className="text-sm font-medium text-zinc-300 mb-2 flex items-center gap-2">
            <GitMerge className="w-3.5 h-3.5" /> Merge source into target
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-2">
            <div>
              <Label htmlFor="td-merge-source-input" className="text-xs text-zinc-400">
                source TD id (will be archived)
              </Label>
              <Input
                id="td-merge-source-input"
                value={mergeSource}
                onChange={(e) => setMergeSource(e.target.value)}
                placeholder="paste source TD id"
                className="font-mono text-xs"
                data-testid="td-merge-source-input"
              />
            </div>
            <div>
              <Label htmlFor="td-merge-target-input" className="text-xs text-zinc-400">
                target TD id (refs repointed here)
              </Label>
              <Input
                id="td-merge-target-input"
                value={mergeTarget}
                onChange={(e) => setMergeTarget(e.target.value)}
                placeholder="paste target TD id"
                className="font-mono text-xs"
                data-testid="td-merge-target-input"
              />
            </div>
          </div>
          <Button
            variant="outline"
            onClick={runMergeDryRun}
            disabled={actionLoading || !mergeSource.trim() || !mergeTarget.trim()}
            data-testid="td-merge-dry-run-btn"
          >
            <Play className="w-3.5 h-3.5 mr-1" /> Merge dry-run
          </Button>

          {mergeDryRun ? (
            <div
              className="mt-2 rounded border border-zinc-700/40 bg-zinc-900/40 p-2 text-xs"
              data-testid="td-merge-dry-run-result"
            >
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <div>
                  <div className="text-zinc-400">active refs to repoint</div>
                  <div className="text-zinc-300">{mergeDryRun.active_references_to_repoint}</div>
                </div>
                <div>
                  <div className="text-zinc-400">historical refs</div>
                  <div className="text-zinc-300">{mergeDryRun.historical_reference_count}</div>
                </div>
                <div>
                  <div className="text-zinc-400">snapshots preserved</div>
                  <div className="text-zinc-300">{String(mergeDryRun.historical_snapshots_preserved)}</div>
                </div>
                <div>
                  <div className="text-zinc-400">blocked</div>
                  <div className="text-zinc-300">{String(mergeDryRun.blocked)}</div>
                </div>
              </div>
              {mergeDryRun.blockers?.length ? (
                <ul className="mt-2 list-disc list-inside text-rose-300">
                  {mergeDryRun.blockers.map((b, i) => (
                    <li key={i} data-testid={`td-merge-blocker-${i}`}>
                      {b.reason}{b.id ? ` (id=${b.id})` : ""}
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          ) : null}

          <div className="mt-2">
            <Label className="text-xs text-zinc-400" htmlFor="td-merge-confirm-input">
              Type exactly to enable merge apply:{" "}
              <span className="font-mono text-amber-300">{APPLY_MERGE}</span>
            </Label>
            <div className="flex flex-wrap gap-2 items-center mt-1">
              <Input
                id="td-merge-confirm-input"
                value={mergeConfirm}
                onChange={(e) => setMergeConfirm(e.target.value)}
                placeholder={APPLY_MERGE}
                className="max-w-md font-mono text-xs"
                data-testid="td-merge-confirm-input"
              />
              <Button
                onClick={applyMerge}
                disabled={!mergeApplyEnabled}
                data-testid="td-merge-apply-btn"
              >
                <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Apply merge
              </Button>
              {!mergeApplyEnabled ? (
                <div
                  className="text-xs text-zinc-400 flex items-center gap-1"
                  data-testid="td-merge-apply-helper"
                >
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />
                  Dry-run (not blocked), then exact phrase required.
                </div>
              ) : null}
            </div>
          </div>
        </div>

        {/* EVENTSFLO-TASK-DEFINITIONS-CLEANUP-AUDIT-LOG-PANEL-V1 */}
        {/* Read-only recent maintenance audit log (last 20). No
            undo / rollback / retry / apply controls. */}
        <div
          className="mt-3 rounded-md border border-zinc-700/40 p-3"
          data-testid="td-cleanup-audit-log-section"
        >
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <div className="text-sm font-medium text-zinc-300 flex items-center gap-2">
              <History className="w-3.5 h-3.5" />
              Recent maintenance audit log
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={exportAuditCsv}
                disabled={csvExporting}
                data-testid="td-cleanup-audit-export-csv-btn"
                title="Download the last 50 audit rows as CSV (read-only)"
              >
                <Download className="w-3.5 h-3.5 mr-1" />
                {csvExporting ? "Exporting…" : "Export CSV"}
              </Button>
              <Button
                variant="outline"
                onClick={fetchAuditLog}
                disabled={auditLoading}
                data-testid="td-cleanup-audit-refresh-btn"
              >
                <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
              </Button>
            </div>
          </div>
          <div
            className="rounded-md border border-zinc-500/30 bg-zinc-500/5 p-2 text-xs text-zinc-300 flex items-start gap-2 mb-2"
            data-testid="td-cleanup-audit-readonly-note"
          >
            <Lock className="w-3.5 h-3.5 mt-0.5" />
            <span>
              Read-only history. No actions can be taken from this
              panel. CSV export uses the same read-only safe
              projection as this panel.
            </span>
          </div>
          {auditLog && Array.isArray(auditLog.rows) && auditLog.rows.length > 0 ? (
            <div className="overflow-auto rounded border border-zinc-700/40 max-h-72">
              <table className="w-full text-xs" data-testid="td-cleanup-audit-table">
                <thead className="bg-zinc-800/60 text-zinc-400 sticky top-0">
                  <tr>
                    <th className="text-left px-2 py-1">When</th>
                    <th className="text-left px-2 py-1">Action</th>
                    <th className="text-left px-2 py-1">Actor</th>
                    <th className="text-left px-2 py-1">Dry run?</th>
                    <th className="text-left px-2 py-1">Production mutation?</th>
                    <th className="text-left px-2 py-1">Affected</th>
                    <th className="text-left px-2 py-1">Warnings</th>
                    <th className="text-left px-2 py-1">Blockers</th>
                    <th className="text-left px-2 py-1">Summary</th>
                  </tr>
                </thead>
                <tbody>
                  {auditLog.rows.map((row, idx) => (
                    <tr
                      key={row.id || `audit-${idx}`}
                      className="odd:bg-zinc-900/30"
                      data-testid={`td-cleanup-audit-row-${idx}`}
                    >
                      <td className="px-2 py-1 text-zinc-300 whitespace-nowrap">
                        {formatWhen(row.created_at)}
                      </td>
                      <td
                        className="px-2 py-1 font-mono text-zinc-300"
                        data-testid={`td-cleanup-audit-row-${idx}-action`}
                      >
                        {row.action}
                      </td>
                      <td className="px-2 py-1 text-zinc-300">
                        {row.actor_email || row.actor_user_id || "—"}
                      </td>
                      <td
                        className="px-2 py-1"
                        data-testid={`td-cleanup-audit-row-${idx}-dry-run`}
                      >
                        {row.dry_run ? (
                          <Badge className="bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
                            yes
                          </Badge>
                        ) : (
                          <Badge className="bg-zinc-500/15 text-zinc-300 border border-zinc-500/30">
                            no
                          </Badge>
                        )}
                      </td>
                      <td
                        className="px-2 py-1"
                        data-testid={`td-cleanup-audit-row-${idx}-production-mutation`}
                      >
                        {row.production_mutation ? (
                          <Badge className="bg-amber-500/15 text-amber-300 border border-amber-500/30">
                            yes
                          </Badge>
                        ) : (
                          <Badge className="bg-zinc-500/15 text-zinc-300 border border-zinc-500/30">
                            no
                          </Badge>
                        )}
                      </td>
                      <td className="px-2 py-1 text-zinc-300">{row.affected_count}</td>
                      <td className="px-2 py-1 text-zinc-300">{row.warning_count}</td>
                      <td className="px-2 py-1 text-zinc-300">{row.blocker_count}</td>
                      <td className="px-2 py-1 text-zinc-300">{row.summary}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-xs text-zinc-500" data-testid="td-cleanup-audit-empty">
              {auditLoading
                ? "Loading recent maintenance activity…"
                : "No recent maintenance activity recorded."}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
